import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Выбор товара лупой (закупка с биржи: строка накладной, «несколько товаров», экран приёмки) —
 * всплывающим окном у кнопки, а не списком в потоке: таблица под ним не сдвигается (владелец 04.11).
 *
 * anchorRef — кнопка-лупа; окно в портале на body, position: fixed по её getBoundingClientRect,
 * снизу или (не хватает места) сверху, едет за прокруткой и ресайзом; закрывается кликом мимо и Esc.
 * На узком экране (< 700 px) — шторка снизу с затемнением. Сверху — «Похожие» (тот же вид, что у
 * строки: выбор обычно в один клик), ниже — поиск по каталогу; архивные — с пометкой «архив».
 * Стрелки и Enter — выбор с клавиатуры (multi: Enter — отметить, Ctrl/⌘+Enter — «Добавить N»).
 * Закрылось — фокус обратно на кнопку, если он был в окне.
 *
 * loadOptions() → Promise<{ similar: [{id, name, is_active, is_hidden, height_cm}], products: [...] }>.
 * footer — { label, onClick } или список таких: действия внизу окна («+ создать «…»», «Несколько товаров…»).
 * multi — выбор нескольких (✓ у строк, внизу «Добавить N товаров» → onSelectMany(список)).
 * Скрытые и архивные — свёрнутой группой «Скрытые и архив (N)» под видимыми.
 * Встроенный ProductSearchDropdown (другие экраны) не меняется.
 */
const WIDTH = 360;
const MAX_HEIGHT = 320;
const GAP = 6;
const NARROW = 700;

const norm = (s) => (s || '').toLowerCase().replace(/ё/g, 'е');

function useNarrow() {
  const get = () => typeof window !== 'undefined' && window.innerWidth < NARROW;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const on = () => setNarrow(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return narrow;
}

export default function ProductPickerPopover({ anchorRef, loadOptions, onSelect, onClose, excludeIds = [], title = 'Выбрать товар', footer = null,
  multi = false, onSelectMany = null }) {
  const narrow = useNarrow();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState([]);        // multi: выбранные
  const [showHidden, setShowHidden] = useState(false);
  const hadFocus = useRef(false);   // фокус был в окне — при закрытии вернуть его на кнопку
  const uid = useMemo(() => `pp${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    const anchor = anchorRef && anchorRef.current;
    return () => {
      const a = document.activeElement;
      if (hadFocus.current && anchor && document.contains(anchor) && (!a || a === document.body || !document.contains(a))) {
        anchor.focus({ preventScroll: true });
      }
    };
  }, [anchorRef]);

  useEffect(() => {
    let alive = true;
    loadOptions()
      .then((o) => { if (alive) setOptions(o || { similar: [], products: [] }); })
      .catch((e) => { if (alive) setError(e.message || 'Не удалось загрузить товары'); });
    return () => { alive = false; };
  }, [loadOptions]);

  // Положение у кнопки: снизу, а не помещается — сверху; по горизонтали — в пределах окна
  useLayoutEffect(() => {
    if (narrow) return undefined;
    const place = () => {
      const el = anchorRef && anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - GAP;
      const up = below < MAX_HEIGHT + 60 && r.top > below;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - WIDTH - 8));
      setPos(up ? { left, bottom: window.innerHeight - r.top + GAP } : { left, top: r.bottom + GAP });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, narrow]);

  // На телефоне поле поиска сами не фокусируем: клавиатура iOS сдвигает окно, и затемнение не
  // накрывает шапку, а под шторкой видна полоса страницы. Ищут — нажав на поле
  useEffect(() => { if (!narrow) inputRef.current?.focus(); }, [narrow]);
  // Шторка открыта — страница под ней не прокручивается (iOS: body fixed с запомненной прокруткой)
  useEffect(() => {
    if (!narrow) return undefined;
    const y = window.scrollY;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, left: b.left, right: b.right, overflow: b.overflow };
    Object.assign(b, { position: 'fixed', top: `-${y}px`, left: '0', right: '0', overflow: 'hidden' });
    return () => {
      Object.assign(b, prev);
      window.scrollTo(0, y);
    };
  }, [narrow]);

  // Клик мимо окна и мимо самой кнопки — закрыть
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && boxRef.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [anchorRef, onClose]);

  const excluded = useMemo(() => new Set(excludeIds.map(String)), [excludeIds]);
  const similar = useMemo(() => (options?.similar || []).filter(p => !excluded.has(String(p.id))), [options, excluded]);
  const results = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return [];
    const words = q.split(/\s+/);
    return (options?.products || [])
      .filter(p => !excluded.has(String(p.id)) && words.every(w => norm(p.name).includes(w)))
      .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1))
      .slice(0, 50);
  }, [options, query, excluded]);
  // Видимые — сразу, скрытые и архив — свёрнутой группой
  const isOff = (p) => !p.is_active || p.is_hidden;
  const list = query.trim() ? results : similar;
  const visible = list.filter(p => !isOff(p));
  const off = list.filter(isOff);
  // Без поиска — «Похожие»; с поиском — найденное. Клавиатура — по видимым и раскрытой группе
  const flat = showHidden ? [...visible, ...off] : visible;
  useEffect(() => { setActive(0); }, [query]);

  const choose = (p) => {
    if (!p) return;
    if (multi) {
      setPicked(cur => (cur.some(x => x.id === p.id) ? cur.filter(x => x.id !== p.id) : [...cur, p]));
      return;
    }
    onSelect(p);
  };
  const confirmMany = () => { if (picked.length && onSelectMany) onSelectMany(picked); };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
    if (multi && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); confirmMany(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(flat.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); choose(flat[active]); }
  };
  // Esc, даже когда фокус ушёл из поля (окно под нами не должно закрыться)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const isPicked = (p) => picked.some(x => x.id === p.id);
  const row = (p, i) => (
    <button key={p.id} id={`${uid}-${i}`} type="button" role="option" aria-selected={multi ? isPicked(p) : i === active}
      onMouseEnter={() => setActive(i)} onClick={() => choose(p)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
        padding: narrow ? '12px 14px' : '8px 12px', border: 'none', textAlign: 'left', cursor: 'pointer',
        background: i === active ? '#F1F6F3' : 'transparent', color: '#1C3D2E', fontSize: 14, fontFamily: 'inherit',
      }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {multi && <span aria-hidden="true" style={{ display: 'inline-block', width: 18, color: '#1F763B', fontWeight: 700 }}>
          {isPicked(p) ? '✓' : ''}</span>}
        {p.name}
        {p.height_cm ? <span style={{ fontSize: 11, color: '#909894', marginLeft: 6 }}>{p.height_cm} см</span> : null}
      </span>
      <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
        {!p.is_active && <span style={{ fontSize: 11, color: '#909894' }}>архив</span>}
        {p.is_active && p.is_hidden && <span style={{ fontSize: 11, color: '#909894' }}>скрыт</span>}
      </span>
    </button>
  );
  const label = (text) => (
    <div style={{ padding: '8px 12px 4px', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#909894', textTransform: 'uppercase' }}>{text}</div>
  );

  const body = (
    <>
      <div style={{ padding: 10, borderBottom: '1px solid #EBEDEC' }}>
        <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onKeyDown}
          placeholder="Найти товар…" aria-label={title} role="combobox" aria-expanded="true" aria-controls={`${uid}-list`}
          aria-activedescendant={flat[active] ? `${uid}-${active}` : undefined}
          aria-describedby={multi && !narrow ? `${uid}-hint` : undefined}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #C1C8C4', '--ch': '5px', '--stroke': '#C1C8C4', fontSize: 14, outline: 'none' }} />
      </div>
      <div role="listbox" id={`${uid}-list`} aria-label={title} aria-multiselectable={multi || undefined}
        style={{ overflowY: 'auto', flex: 1, paddingBottom: 6 }}>
        {error && <div style={{ padding: 12, color: '#B0603C', fontSize: 13 }}>{error}</div>}
        {!error && !options && <div style={{ padding: 12, color: '#909894', fontSize: 13 }}>Загрузка…</div>}
        {options && (
          list.length ? (
            <>
              {label(query.trim() ? 'Найдено' : 'Похожие')}
              {visible.map((p, i) => row(p, i))}
              {off.length > 0 && (
                <button type="button" aria-expanded={showHidden} onClick={() => setShowHidden(v => !v)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: narrow ? '12px 14px' : '8px 12px', border: 'none',
                    background: 'none', color: '#5E6561', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {showHidden ? '▾' : '▸'} Скрытые и архив ({off.length})
                </button>
              )}
              {showHidden && off.map((p, i) => row(p, visible.length + i))}
            </>
          ) : (
            <div style={{ padding: 12, color: '#909894', fontSize: 13 }}>
              {query.trim() ? 'Ничего не найдено' : 'Похожих нет — найдите по названию'}
            </div>
          )
        )}
      </div>
      {multi && (
        <>
          <button type="button" disabled={!picked.length} onClick={confirmMany} onKeyDown={onKeyDown}
            className="btn btn-primary" style={{ margin: '10px 10px 4px', minHeight: 44 }}>
            {picked.length ? `Добавить ${picked.length} ${picked.length === 1 ? 'товар' : picked.length < 5 ? 'товара' : 'товаров'}` : 'Отметьте товары'}
          </button>
          {!narrow && <div id={`${uid}-hint`} style={{ padding: '0 12px 8px', fontSize: 11, color: '#909894' }}>Enter — отметить · Ctrl+Enter — добавить</div>}
        </>
      )}
      {(Array.isArray(footer) ? footer : footer ? [footer] : []).filter(Boolean).map(f => (
        <button key={f.label} type="button" onClick={f.onClick}
          style={{ padding: narrow ? '12px 14px' : '9px 12px', border: 'none', borderTop: '1px solid #EBEDEC', background: '#F9F9F6',
            color: '#1C3D2E', fontSize: 13, fontWeight: 600, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
          {f.label}
        </button>
      ))}
    </>
  );

  if (narrow) {
    return createPortal(
      // Поверх всего приложения (шапка, вкладки, нижняя панель), до самого низа экрана
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1900 }}>
        <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(28,61,46,0.35)' }} />
        <div ref={boxRef} role="dialog" aria-label={title} onFocus={() => { hadFocus.current = true; }} style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '75vh', display: 'flex', flexDirection: 'column',
          background: '#fff', boxShadow: '0 -8px 24px rgba(28,61,46,.12)', paddingBottom: 'env(safe-area-inset-bottom)',
          boxSizing: 'border-box',
        }}>
          <div aria-hidden="true" style={{ width: 40, height: 4, background: '#C1C8C4', borderRadius: 2, margin: '8px auto 2px' }} />
          <div style={{ padding: '4px 14px 0', fontWeight: 600, fontSize: 15, color: '#1C3D2E' }}>{title}</div>
          {body}
        </div>
      </div>,
      document.body,
    );
  }
  if (!pos) return null;
  return createPortal(
    <div ref={boxRef} role="dialog" aria-label={title} onFocus={() => { hadFocus.current = true; }} style={{
      position: 'fixed', ...pos, width: WIDTH, maxHeight: MAX_HEIGHT, zIndex: 1600, display: 'flex', flexDirection: 'column',
      background: '#fff', border: '1px solid #DCDDD3', '--ch': '8px', '--stroke': '#DCDDD3',
      boxShadow: '0 8px 24px rgba(28,61,46,.12)',
    }}>
      {body}
    </div>,
    document.body,
  );
}
